import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ReportKpiProps {
  label: string;
  /** Already formatted — "63", "38 mins", "4%". */
  value: string;
  icon: ReactNode;
  /** One line under the value. */
  hint?: string;
  /**
   * Change against the previous period, in whole percent. Null hides the
   * badge; it is drawn only where a previous period exists to compare with.
   */
  change?: number | null;
  /** Shown instead of a change when there is no previous period to compare. */
  isNew?: boolean;
}

/**
 * A headline figure for the report, with how it moved since the last period.
 *
 * The movement wears an arrow and a sign as well as a colour, so it reads the
 * same without colour. Up is good on every figure this report shows — more
 * sign-ins, more time in the app — so green means up throughout.
 */
export function ReportKpi({ label, value, icon, hint, change, isNew = false }: ReportKpiProps) {
  const direction = change === undefined || change === null ? null : change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

  return (
    <div className="card flex flex-col gap-3 p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-[650] text-ink-2">{label}</p>
        <span className="grid h-9 w-9 place-items-center rounded-field bg-accent-soft text-accent" aria-hidden>
          {icon}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <p className="text-[30px] font-[750] leading-none tracking-tight text-ink">{value}</p>

        {direction && (
          <span
            className={cn(
              'pill',
              direction === 'up' && 'bg-ok-bg text-ok',
              direction === 'down' && 'bg-crit-bg text-crit',
              direction === 'flat' && 'bg-surface-2 text-ink-3',
            )}
          >
            {direction === 'up' && <ArrowUpRight className="h-3 w-3" aria-hidden />}
            {direction === 'down' && <ArrowDownRight className="h-3 w-3" aria-hidden />}
            {direction === 'flat' && <Minus className="h-3 w-3" aria-hidden />}
            {change !== null && change !== undefined && `${change > 0 ? '+' : ''}${change}%`}
            <span className="sr-only"> compared with the previous period</span>
          </span>
        )}
        {!direction && isNew && <span className="pill bg-info-bg text-info">New this period</span>}
      </div>

      {hint && <p className="text-xs text-ink-3">{hint}</p>}
    </div>
  );
}
